import {
  SelectedElementContainer,
  type PageStructure,
  useUpdateBounds
} from '@st/document-viewer-kit'
import type { ElementStatus, ToolMessage } from '@st/document-viewer-kit/plugins/annotations'
import { clsx } from 'clsx'
import { denormalizeRect, normalizeRect, type Rect } from '@st/geom'
import { match, P } from '@st/pattern'
import { TextLayout } from '@st/document-rendering'
import {
  constrainPageRectMove,
  constrainPageRectResize
} from '~/st-doc-viewer/page-annotation-bounds'
import { DeleteIcon } from '~/components/icons'
import type {
  ViewerFieldAnnotation,
  ContractFieldAnnotation
} from '~/st-doc-viewer/viewer-annotations'
import type { FieldToolState } from '~/st-doc-viewer/annotations'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import {
  signerFieldAppearance,
  signerFieldAriaLabel,
  signerFieldFonts,
  signerFieldShape
} from './signer-field-appearance'

const minimumFieldSize = { width: 64, height: 12 }

export function FieldAnnotationElement({
  annotation,
  status,
  send,
  page
}: {
  annotation: ViewerFieldAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<ViewerFieldAnnotation, FieldToolState>) => void
}) {
  const bounds = denormalizeRect(annotation.bounds, page.bounds)
  const isSelected = status != 'none'

  return match(annotation)
    .with({ mode: P.union('editing', 'viewing') }, (annotation) =>
      match(annotation.mode == 'editing' && isSelected)
        .with(true, () => (
          <EditableFieldAnnotation
            annotation={annotation}
            page={page}
            initialBounds={bounds}
            send={send}
          />
        ))
        .with(false, () => (
          <ContractSignerField
            annotation={annotation}
            bounds={bounds}
            isSelected={isSelected}
            onClick={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
          />
        ))
        .exhaustive()
    )
    .with({ mode: 'signing' }, (annotation) => {
      const displayValue = annotation.value.trim()
      const isSignature = annotation.field.type == 'signature'
      const canSelect = annotation.canSign
      const decorationLabel = annotation.field.decoration && annotation.field.label
      const content = match(displayValue)
        .with('', () =>
          match(decorationLabel)
            .with(undefined, () =>
              match(isSignature && annotation.canSign)
                .with(true, () => 'Sign')
                .with(false, () => annotation.label)
                .exhaustive()
            )
            .otherwise((label) => (
              <span className="text-current/70 font-sans text-[9px] font-medium leading-none">
                {label}
              </span>
            ))
        )
        .otherwise((value) => (
          <SignedFieldValue
            value={value}
            signature={isSignature}
            bounds={bounds}
            label={decorationLabel}
          />
        ))

      return (
        <button
          type="button"
          disabled={!canSelect}
          aria-label={fieldAnnotationLabel(annotation)}
          onClick={() => {
            send({ type: 'selectAnnotation', annotationId: annotation.id })
          }}
          className={clsx(
            'absolute overflow-visible rounded-sm border text-left',
            canSelect ? 'cursor-pointer' : 'cursor-default',
            isSignature && displayValue
              ? 'font-signature text-lg font-normal'
              : 'text-xs font-semibold',
            isSelected
              ? 'border-blue-600 bg-blue-100/90 text-blue-950 ring-2 ring-blue-500'
              : displayValue
              ? 'border-green-600 bg-green-100/80 text-green-950'
              : annotation.muted
              ? 'border-gray-300 bg-gray-100/70 text-gray-500'
              : !annotation.canSign
              ? 'border-blue-500 bg-blue-100/50 text-blue-950'
              : 'border-amber-500 bg-amber-100/80 text-amber-950'
          )}
          style={{
            top: bounds.y,
            left: bounds.x,
            width: bounds.width,
            height: bounds.height
          }}
        >
          {content}
        </button>
      )
    })
    .with({ mode: 'data' }, (annotation) => (
      <div
        onClick={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        style={{
          position: 'absolute',
          boxSizing: 'border-box',
          top: bounds.y,
          left: bounds.x,
          width: bounds.width,
          height: bounds.height,
          cursor: 'pointer'
        }}
      >
        <div
          className={clsx(
            'h-full w-full rounded-sm',
            isSelected
              ? 'bg-yellow-400/40 shadow-[inset_0_0_0_1px_rgba(161,98,7,0.45)]'
              : 'bg-blue-500/10 shadow-[inset_0_0_0_1px_rgba(37,99,235,0.28)]'
          )}
        />
      </div>
    ))
    .exhaustive()
}

function SignedFieldValue({
  value,
  signature,
  bounds,
  label
}: {
  value: string
  signature: boolean
  bounds: Rect
  label: string | undefined
}) {
  const font = match(signature)
    .with(true, () => signerFieldFonts.signature)
    .with(false, () => signerFieldFonts.value)
    .exhaustive()
  const inset = match(label)
    .with(undefined, () => ({ left: 1, right: 1, bottom: 1 }))
    .otherwise(() => ({ left: 6, right: 6, bottom: 6 }))
  const width = Math.max(bounds.width - inset.left - inset.right, 1)
  const measured = TextLayout.layout(value, font, 1.1).width
  const size = font.size * Math.min(1, width / Math.max(measured, 1))
  // SVG's text y is the baseline. Keep the field/hit target unchanged while
  // letting glyph descenders extend below it, just as in the final PDF.
  return (
    <svg
      width={bounds.width}
      height={bounds.height}
      viewBox={`0 0 ${bounds.width} ${bounds.height}`}
      style={{ left: -1, top: -1, fontKerning: 'none' }}
      className="pointer-events-none absolute overflow-visible"
      aria-hidden="true"
    >
      {label && (
        <text
          x={6}
          y={5 + signerFieldFonts.label.size * 0.718}
          fontFamily={signerFieldFonts.label.family}
          fontSize={signerFieldFonts.label.size}
          fontWeight="normal"
          fill="#555555"
        >
          {label}
        </text>
      )}
      <text
        x={inset.left}
        y={bounds.height - inset.bottom}
        fontFamily={font.family}
        fontSize={size}
        fontWeight="normal"
        fill="currentColor"
      >
        {TextLayout.displayText(value, font)}
      </text>
    </svg>
  )
}

function EditableFieldAnnotation({
  annotation,
  page,
  initialBounds,
  send
}: {
  annotation: ContractFieldAnnotation & { mode: 'editing' | 'viewing' }
  page: PageStructure
  initialBounds: ReturnType<typeof denormalizeRect>
  send: (message: ToolMessage<ViewerFieldAnnotation, FieldToolState>) => void
}) {
  const { bounds, onMove, onResize } = useUpdateBounds({
    initialBounds,
    constrain: (event) => {
      if (event.type == 'move') return constrainPageRectMove(event.newBounds, page.bounds)
      return constrainFieldRectResize(event.newBounds, event.oldBounds, page.bounds)
    },
    onCommit: (event) =>
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: {
          ...annotation,
          bounds: normalizeRect(event.newBounds, page.bounds),
          field: { ...annotation.field, rect: normalizeRect(event.newBounds, page.bounds) }
        }
      })
  })

  return (
    <SelectedElementContainer
      bounds={bounds}
      onMove={onMove}
      onResize={onResize}
      resizable={true}
      ariaLabel={fieldAnnotationLabel(annotation)}
      selectionBorder={false}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            ariaLabel="Delete field"
            icon={<DeleteIcon className="size-6" />}
            onClick={() => send({ type: 'deleteAnnotation', annotationId: annotation.id })}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <ContractSignerFieldBody annotation={annotation} selected={true} />
    </SelectedElementContainer>
  )
}

function ContractSignerField({
  annotation,
  bounds,
  isSelected,
  onClick
}: {
  annotation: ContractFieldAnnotation & { mode: 'editing' | 'viewing' }
  bounds: ReturnType<typeof denormalizeRect>
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={fieldAnnotationLabel(annotation)}
      onClick={onClick}
      className="absolute overflow-visible text-left"
      style={{
        top: bounds.y,
        left: bounds.x,
        width: bounds.width,
        height: bounds.height
      }}
    >
      <ContractSignerFieldBody annotation={annotation} selected={isSelected} />
    </button>
  )
}

function ContractSignerFieldBody({
  annotation,
  selected
}: {
  annotation: ContractFieldAnnotation & { mode: 'editing' | 'viewing' }
  selected: boolean
}) {
  const appearance = signerFieldAppearance(annotation.signerRole)
  const fieldLabel = match(annotation.field.type)
    .with(P.union('signature', 'date'), (fieldType) =>
      signerFieldAriaLabel(annotation.signerRole, fieldType)
    )
    .otherwise(() => annotation.field.label)
  const selectedClass = match(selected)
    .with(true, () => `ring-1 ring-offset-1 ${appearance.selected}`)
    .otherwise(() => undefined)

  return (
    <div className={clsx(appearance.field, selectedClass, signerFieldShape, 'h-full w-full')}>
      {fieldLabel}
    </div>
  )
}

function constrainFieldRectResize(newBounds: Rect, oldBounds: Rect, pageBounds: Rect) {
  const resized = constrainPageRectResize(newBounds, pageBounds)

  if (resized.width < minimumFieldSize.width) {
    if (newBounds.x != oldBounds.x) {
      resized.x = oldBounds.x + oldBounds.width - minimumFieldSize.width
    }
    resized.width = minimumFieldSize.width
  }

  if (resized.height < minimumFieldSize.height) {
    if (newBounds.y != oldBounds.y) {
      resized.y = oldBounds.y + oldBounds.height - minimumFieldSize.height
    }
    resized.height = minimumFieldSize.height
  }

  return constrainPageRectResize(resized, pageBounds)
}

function fieldAnnotationLabel(annotation: ViewerFieldAnnotation) {
  return match(annotation)
    .with({ mode: 'data' }, ({ label }) => label)
    .with({ mode: P.union('editing', 'viewing') }, ({ field }) => field.label)
    .with({ mode: 'signing' }, ({ label, value }) =>
      match(value.trim())
        .with('', () => label)
        .otherwise((text) => `${label}: ${text}`)
    )
    .exhaustive()
}
